import { ActivityIcon } from 'lucide-react'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Item, ItemContent, ItemDescription, ItemGroup, ItemTitle } from '@/components/ui/item'
import { View } from '@/components/view'
import { choiceLabel, eventLabel, formatDateTime, relativeTime, sourceLabel } from '@/lib/format'
import { WAITING_CHOICES } from '@/lib/lists'
import { useSession, useUserNow } from '@/lib/session'

function describe(type: string, data: Record<string, unknown>): string | null {
  if (type === 'debug.ping' && typeof data.message === 'string') return data.message
  if (type === 'clock.changed' && typeof data.now === 'string') {
    return `Now ${formatDateTime(new Date(data.now))}`
  }
  if (type === 'session.started') return 'New session'
  if (type === 'item.suggested' && typeof data.choice === 'string') return choiceLabel(data.choice)
  if (type === 'item.kind_changed' && typeof data.to === 'string') {
    const choice = WAITING_CHOICES.find((c) => c.kind === data.to)
    return choice ? `Waiting: ${choice.label.toLowerCase()}` : null
  }
  if (type.startsWith('connection.') && typeof data.connector === 'string') {
    const name = sourceLabel(data.connector)
    if (typeof data.reason === 'string') return `${name}: ${data.reason}`
    if (typeof data.accountEmail === 'string') return `${name} · ${data.accountEmail}`
    return name
  }
  if (type === 'outbox.failed' && typeof data.error === 'string') return data.error
  if (type === 'outbox.withdrawn' && typeof data.reason === 'string') return data.reason
  if (type.startsWith('outbox.') && Array.isArray(data.recipients)) {
    return `To ${data.recipients.filter((r) => typeof r === 'string').join(', ')}`
  }
  return null
}

/** Live feed of changes since this page was opened. */
export function ActivityPage() {
  const { recent } = useSession()
  const now = useUserNow()
  return (
    <View
      name="activity"
      title="Activity"
      description="Changes as they happen, from you, the agent and your connected apps."
    >
      {recent.length === 0 ? (
        <Empty className="flex-none border border-dashed py-16" data-testid="empty-state">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ActivityIcon aria-hidden="true" />
            </EmptyMedia>
            <EmptyTitle>Nothing new yet</EmptyTitle>
            <EmptyDescription>New activity appears here live while hugo is open.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <ItemGroup aria-label="Recent activity" data-testid="activity-list">
          {recent.map((event) => {
            const detail = describe(event.type, event.data)
            return (
              <Item key={event.id} variant="outline" data-event-type={event.type}>
                <ItemContent>
                  <ItemTitle>{eventLabel(event.type)}</ItemTitle>
                  {detail ? <ItemDescription>{detail}</ItemDescription> : null}
                </ItemContent>
                <time
                  dateTime={event.createdAt}
                  title={formatDateTime(new Date(event.createdAt))}
                  className="shrink-0 text-xs text-muted-foreground"
                >
                  {relativeTime(new Date(event.createdAt), now)}
                </time>
              </Item>
            )
          })}
        </ItemGroup>
      )}
    </View>
  )
}
